/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: murmur-band energy evidence and per-cycle acoustic activity
// (core-viewer.js 3533-3729, 4031-4037).
// The MURMUR trace global becomes an explicit argument (decoded via traces.js);
// D.duration and TURBULENT_RF_MODEL likewise.

import { mean, sd, percentile, valuesForWindow, peakCount, sustainedSeconds } from "./stats"
import { cycleAuditId } from "./cycles"
import {
  MURMUR_GUIDE_THRESHOLD,
  MURMUR_MIN_MEAN,
  MURMUR_MIN_SUSTAINED_S,
  TURBULENT_EDGE_TRIM_S,
  MIN_ENERGY_RELIABLE_CYCLES,
} from "./constants"

export function energyStrengthTier(value) {
  if (!Number.isFinite(Number(value))) return { label: "N/A", className: "unavailable" }
  const v = Math.max(0, Math.min(1, Number(value)))
  if (v >= 0.45) return { label: "High", className: "high" }
  if (v >= 0.18) return { label: "Moderate", className: "moderate" }
  return { label: "Low", className: "low" }
}

export function adaptiveEnergyThreshold(values) {
  if (!values.length) return MURMUR_GUIDE_THRESHOLD
  const p50 = percentile(values, 0.5)
  const p90 = percentile(values, 0.9)
  const spread = Math.max(0, p90 - p50)
  return Math.min(0.92, Math.max(MURMUR_GUIDE_THRESHOLD, p50 + Math.max(0.08, spread * 0.55)))
}

export function energyWindowHit(values, duration, threshold = MURMUR_GUIDE_THRESHOLD, baseline = null) {
  if (!values.length) return false
  const peak = Math.max(...values)
  const avg = mean(values)
  const med = percentile(values, 0.5)
  const p25 = percentile(values, 0.25)
  const p75 = percentile(values, 0.75)
  const localProminence = peak - med
  const globalLift = baseline === null ? localProminence : Math.max(peak - baseline, avg - baseline)
  const hasShape = localProminence >= 0.08 || (p75 - p25 >= 0.05 && globalLift >= 0.06) || globalLift >= 0.12
  return (
    peak >= threshold &&
    mean(values) >= MURMUR_MIN_MEAN &&
    sustainedSeconds(values, threshold, duration) >= MURMUR_MIN_SUSTAINED_S &&
    hasShape
  )
}

// murmurTrace: Float32Array from decodeAnalysisTraces; duration: recording seconds.
export function murmurBandEnergyEvidence(murmurTrace, duration) {
  if (!murmurTrace || !murmurTrace.length || !duration) {
    return {
      detected: false,
      substantial: false,
      threshold: MURMUR_GUIDE_THRESHOLD,
      baseline: null,
      mean: 0,
      peak: 0,
      p75: 0,
      strengthValue: 0,
      strengthTier: "Low",
      coveragePct: 0,
      sustainedSeconds: 0,
    }
  }
  const values = Array.from(murmurTrace)
  const threshold = adaptiveEnergyThreshold(values)
  const baseline = percentile(values, 0.5)
  const avg = mean(values)
  const p75 = percentile(values, 0.75)
  const peak = Math.max(...values)
  const strengthValue = Math.max(avg, p75)
  const strengthTier = energyStrengthTier(strengthValue)
  const above = values.filter((v) => v >= threshold).length
  const coveragePct = (100 * above) / values.length
  const sustained = sustainedSeconds(values, threshold, duration)
  const detected = energyWindowHit(values, duration, threshold, baseline)
  const substantial = detected && strengthTier.label !== "Low"
  return {
    detected,
    substantial,
    threshold,
    baseline,
    mean: avg,
    peak,
    p75,
    strengthValue,
    strengthTier: strengthTier.label,
    coveragePct,
    sustainedSeconds: sustained,
  }
}

export function acousticActivityForCycles(
  murmurTrace,
  duration,
  turbulentModel,
  cycles,
  purpose = "Sensitive acoustic activity for HCP review; not a classifier."
) {
  if (!murmurTrace || !murmurTrace.length) return null
  if (cycles.length < MIN_ENERGY_RELIABLE_CYCLES) return null
  const sysVals = [],
    diaVals = []
  const sysCycleMeans = [],
    diaCycleMeans = []
  const sysCyclePeaks = [],
    diaCyclePeaks = []
  const sysPeakCounts = [],
    diaPeakCounts = []
  const sysWindows = [],
    diaWindows = []
  const phaseWindows = []
  const scoredCycleIds = []
  const edgeProximityCycleIds = []
  let sysHit = 0,
    diaHit = 0
  let scoredCycles = 0
  const exclusionPad =
    turbulentModel && typeof turbulentModel.s1s2_exclusion_pad_s === "number"
      ? turbulentModel.s1s2_exclusion_pad_s
      : 0.05
  cycles.forEach((c) => {
    scoredCycles += 1
    const id = cycleAuditId(c)
    scoredCycleIds.push(id)
    if (c.s1.t < TURBULENT_EDGE_TRIM_S || c.nextS1.t > duration - TURBULENT_EDGE_TRIM_S) edgeProximityCycleIds.push(id)
    const sysStart = Math.min(c.s2.t, (c.s1.off ?? c.s1.t) + exclusionPad)
    const sysEnd = Math.max(sysStart, (c.s2.on ?? c.s2.t) - exclusionPad)
    const diaStart = Math.min(c.nextS1.t, (c.s2.off ?? c.s2.t) + exclusionPad)
    const diaEnd = Math.max(diaStart, (c.nextS1.on ?? c.nextS1.t) - exclusionPad)
    const sys = valuesForWindow(murmurTrace, sysStart, sysEnd, duration)
    const dia = valuesForWindow(murmurTrace, diaStart, diaEnd, duration)
    sysVals.push(...sys)
    diaVals.push(...dia)
    if (sys.length) {
      sysWindows.push({ values: sys, duration: sysEnd - sysStart })
      phaseWindows.push({
        cycleId: id,
        phase: "sys",
        start: sysStart,
        end: sysEnd,
        duration: sysEnd - sysStart,
        meanEnergy: mean(sys),
        peakEnergy: Math.max(...sys),
        medianEnergy: percentile(sys, 0.5),
        p75Energy: percentile(sys, 0.75),
      })
    }
    if (dia.length) {
      diaWindows.push({ values: dia, duration: diaEnd - diaStart })
      phaseWindows.push({
        cycleId: id,
        phase: "dia",
        start: diaStart,
        end: diaEnd,
        duration: diaEnd - diaStart,
        meanEnergy: mean(dia),
        peakEnergy: Math.max(...dia),
        medianEnergy: percentile(dia, 0.5),
        p75Energy: percentile(dia, 0.75),
      })
    }
  })
  if (scoredCycles < 3 || (!sysVals.length && !diaVals.length)) return null
  const allEnergyVals = sysVals.concat(diaVals)
  const energyBaseline = percentile(allEnergyVals, 0.5)
  const activityThreshold = adaptiveEnergyThreshold(allEnergyVals)
  sysWindows.forEach((w) => {
    sysCycleMeans.push(mean(w.values))
    sysCyclePeaks.push(Math.max(...w.values))
    const nPeaks = peakCount(w.values, activityThreshold)
    sysPeakCounts.push(nPeaks)
    if (energyWindowHit(w.values, w.duration, activityThreshold, energyBaseline)) sysHit += 1
  })
  diaWindows.forEach((w) => {
    diaCycleMeans.push(mean(w.values))
    diaCyclePeaks.push(Math.max(...w.values))
    const nPeaks = peakCount(w.values, activityThreshold)
    diaPeakCounts.push(nPeaks)
    if (energyWindowHit(w.values, w.duration, activityThreshold, energyBaseline)) diaHit += 1
  })
  const peak = (arr) => (arr.length ? Math.max(...arr) : 0)
  const sysPeakCount = sysPeakCounts.reduce((a, b) => a + b, 0)
  const diaPeakCount = diaPeakCounts.reduce((a, b) => a + b, 0)
  const peakTotal = sysPeakCount + diaPeakCount
  const sysEnergyTotal = sysVals.reduce((a, b) => a + b, 0)
  const diaEnergyTotal = diaVals.reduce((a, b) => a + b, 0)
  const energyTotal = sysEnergyTotal + diaEnergyTotal
  const sysMean = mean(sysVals)
  const diaMean = mean(diaVals)
  const sysPeak = peak(sysVals)
  const diaPeak = peak(diaVals)
  const features = {
    n_cycles: scoredCycles,
    sys_cycle_pct: (100 * sysHit) / scoredCycles,
    dia_cycle_pct: (100 * diaHit) / scoredCycles,
    sys_mean: sysMean,
    dia_mean: diaMean,
    sys_peak: sysPeak,
    dia_peak: diaPeak,
    sys_peak_count: sysPeakCount,
    dia_peak_count: diaPeakCount,
    sys_peaks_per_cycle: sysPeakCount / scoredCycles,
    dia_peaks_per_cycle: diaPeakCount / scoredCycles,
    peak_phase_balance: peakTotal ? (sysPeakCount - diaPeakCount) / peakTotal : 0,
    sys_energy_share: energyTotal ? sysEnergyTotal / energyTotal : 0,
    dia_energy_share: energyTotal ? diaEnergyTotal / energyTotal : 0,
    mean_phase_delta: sysMean - diaMean,
    peak_phase_delta: sysPeak - diaPeak,
    sys_peak_cv: sysCyclePeaks.length ? sd(sysCyclePeaks) / (mean(sysCyclePeaks) + 1e-9) : 0,
    dia_peak_cv: diaCyclePeaks.length ? sd(diaCyclePeaks) / (mean(diaCyclePeaks) + 1e-9) : 0,
    sys_mean_cv: sysCycleMeans.length ? sd(sysCycleMeans) / (mean(sysCycleMeans) + 1e-9) : 0,
    dia_mean_cv: diaCycleMeans.length ? sd(diaCycleMeans) / (mean(diaCycleMeans) + 1e-9) : 0,
    activity_threshold: activityThreshold,
    activity_baseline: energyBaseline,
  }
  return {
    layer: "visual_acoustic_activity",
    purpose,
    cycleAlignment: {
      policy: "score_exact_cycle_list_supplied_by_timing_summary",
      scoredCycleIds,
      edgeProximityCycleIds,
      edgeTrimSeconds: TURBULENT_EDGE_TRIM_S,
      edgeTrimBehavior: "audit_only_for_aligned_energy_v1",
    },
    cycles: scoredCycles,
    sysMean,
    sysPeak,
    sysCyclePct: features.sys_cycle_pct,
    diaMean,
    diaPeak,
    diaCyclePct: features.dia_cycle_pct,
    phaseWindows,
    features,
  }
}
